/**
 * Detects if localStorage is accessible in the current context.
 * Returns an object with access status and error information.
 */
export const detectStorageAccess = () => {
    const testKey = '__storage_test__';

    try {
        // Test if localStorage exists
        if (typeof localStorage === 'undefined') {
            return {
                hasAccess: false,
                error: 'STORAGE_UNDEFINED',
                message: 'localStorage is not available in this browser'
            };
        }

        // Test if we can write to localStorage
        localStorage.setItem(testKey, 'test');
        localStorage.removeItem(testKey);

        return {
            hasAccess: true,
            error: null,
            message: null
        };
    } catch (e) {
        // Determine the type of error
        let errorType = 'STORAGE_ERROR';
        let message = e.message;

        if (e.name === 'SecurityError' || e.message.includes('SecurityError')) {
            errorType = 'SECURITY_ERROR';
            message = 'Storage access blocked by browser security policy';
        } else if (e.name === 'QuotaExceededError') {
            errorType = 'QUOTA_EXCEEDED';
            message = 'Storage quota exceeded';
        } else if (e.message.includes('third-party') || e.message.includes('cross-origin')) {
            errorType = 'THIRD_PARTY_BLOCKED';
            message = 'Third-party storage access blocked';
        }

        return {
            hasAccess: false,
            error: errorType,
            message: message,
            originalError: e
        };
    }
};

/**
 * Detects if we're running in a cross-origin popup context
 */
export const isThirdPartyContext = () => {
    try {
        // If we have an opener and can't access its location, we're in a cross-origin context
        if (window.opener) {
            try {
                // Try to access opener's location - will throw if cross-origin
                // eslint-disable-next-line no-unused-vars
                const openerLocation = window.opener.location.href;
                return false; // Same origin
            } catch (e) {
                return true; // Cross-origin
            }
        }
        return false;
    } catch (e) {
        return false;
    }
};

/**
 * Gets a user-friendly error message for storage access issues
 */
export const getStorageErrorHelp = (storageCheck) => {
    if (storageCheck.hasAccess) {
        return null;
    }

    const isThirdParty = isThirdPartyContext();

    switch (storageCheck.error) {
        case 'SECURITY_ERROR':
        case 'THIRD_PARTY_BLOCKED':
            return {
                title: 'Storage Access Blocked',
                message: isThirdParty
                    ? 'Your browser is blocking storage access because this popup was opened from another website. This is a security feature in modern browsers.'
                    : 'Your browser security settings are preventing storage access.',
                suggestions: [
                    'Open NslMail in a new tab directly (not from the bookmarklet)',
                    'Try a different browser',
                    'Check your browser\'s privacy/security settings',
                    'In Safari: Disable "Prevent Cross-Site Tracking"',
                    'In Firefox: Check "Enhanced Tracking Protection" settings'
                ]
            };
        case 'QUOTA_EXCEEDED':
            return {
                title: 'Storage Full',
                message: 'Your browser storage is full.',
                suggestions: [
                    'Clear your browser cache and cookies',
                    'Close some browser tabs',
                    'Free up disk space'
                ]
            };
        default:
            return {
                title: 'Storage Access Error',
                message: storageCheck.message || 'An unknown error occurred accessing storage.',
                suggestions: [
                    'Try refreshing the page',
                    'Try a different browser',
                    'Contact support if the problem persists'
                ]
            };
    }
};
